import React from 'react'
import { Row, Col } from 'react-bootstrap'
import TimePicker from 'Components/_newComponents/TimePicker'
import Stepper from 'Components/_newComponents/Stepper'
import TrashCanIcon from 'Assets/Images/icons/newIcons/trashCanIcon'
import ArrowIcon from 'Assets/Images/icons/newIcons/arrowIcon'
import TextField from 'material-ui/TextField'
import Paper from 'material-ui/Paper'
import List from 'material-ui/List'
import { formatTimeRange, getLocationIcons } from '../../../utils'
import {
  getWeatherIcon,
  getTimeIcon,
  convertTimeToDisplayFormat,
  getCvrGradientStyles,
} from '../utils/'
import TooltipModal from 'Components/TooltipModal'
import ThumbUpIcon from '../../../../../../Assets/Images/icons/newIcons/thumbUp'
import ThumbDownIcon from '../../../../../../Assets/Images/icons/newIcons/thumbDown'

const rowStyles = {
  rowWrapper: {
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 8,
    border: '1px solid #E2E2FC',
    padding: '14px 0px',
    margin: 0,
    marginBottom: 10,
    fontSize: 16,
  },
  row: {
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    margin: 0,
    justifyContent: 'space-between',
  },

  dateColumn: {
    padding: '0 0 0 10px',
    fontWeight: 400,
  },

  missionColumn: {
    padding: 0,
    display: 'flex',
    justifyContent: 'center',
  },

  buttonColumn: {
    padding: 0,
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '4px',
  },

  deleteButton: {
    border: 'none',
    padding: 0,
    backgroundColor: 'transparent',
    width: 16,
    height: 16,
  },
  arrowButton: {
    backgroundColor: 'transparent',
    border: 'none',
    display: 'flex',
    cursor: 'pointer',
  },
  detailsTable: {
    margin: '10px 16px',
  },
  detailsHeader: {
    padding: '12px 16px 12px 34px',
    borderBottom: '1px solid #E2E2FC',
  },
  detailsHeaderCell: {
    fontSize: 12,
    fontWeight: 700,
    color: '#707087',
    display: 'flex',
    padding: 0,
  },
  detailsRow: isEdit => ({
    display: 'flex',
    alignItems: 'center',
    padding: '12px 16px 12px 34px',
    borderBottom: '1px solid #E2E2FC',
    ...(!isEdit && { margin: 0 }),
  }),
  detailsRowLast: isEdit => ({
    padding: '12px 16px 0px 34px',
    borderBottom: 'none',
    display: 'flex',
    alignItems: 'center',
    ...(!isEdit && { margin: 0 }),
  }),

  detailsCell: {
    fontSize: 16,
    color: '#011533',
    display: 'flex',
    alignItems: 'center',
    lineHeight: '24px',
    gap: '6px',
    padding: 0,
  },
  ratingCell: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: '8px',
  },
  ratingButton: isDisabled => ({
    background: 'transparent',
    border: 'none',
    padding: 0,
    cursor: isDisabled ? 'not-allowed' : 'pointer',
    opacity: isDisabled ? 0.3 : 1,
  }),
  feedbackRow: {
    margin: 0,
    display: 'flex',
    alignItems: 'stretch',
    padding: '8px 16px 8px 16px',
    borderBottom: '1px solid #E2E2FC',
  },
  feedbackLabel: {
    fontSize: 16,
    fontWeight: 700,
    color: '#686890',
    marginBottom: 8,
  },
  feedbackBox: {
    backgroundColor: '#f7f7f9',
    borderRadius: 6,
    paddingLeft: '12px',
  },
  feedbackLimit: {
    marginTop: 10,
    color: '#686890',
    fontSize: 14,
  },
  cvrBadge: {
    width: 72,
    height: 30,
    borderRadius: 4,
    fontSize: 16,
    fontWeight: 400,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    color: '#707087',
  },
  errorWeatherHintWrapper: {
    marginRight: '10px',
    marginLeft: 'auto',
  },
  locationIconWrapper: {
    width: 'max-content',
    gap: '10px',
    display: 'flex',
    alignItems: 'center',
  },
  locationNameWrapper: {
    fontSize: 16,
    color: '#011533',
    display: 'flex',
    alignItems: 'center',
    lineHeight: '24px',
    gap: '6px',
    padding: '6px 10px 6px',
    marginRight: '10px',
  },
  warningWeatherMessage: {
    border: '1px solid #FFA4B5',
    borderRadius: 10,
  },
}

const SelectLocation = ({ value, onChange, locations }) => {
  const [locationSearch, setLocationSearch] = React.useState('')
  const [showLocationResults, setShowLocationResults] = React.useState(false)
  const [locationResults, setLocationResults] = React.useState([])
  const [loading, setLoading] = React.useState(false)
  const locationSearchRef = React.useRef(null)

  const handleLocationSearch = event => {
    const searchTerm = event.target.value
    setLocationSearch(searchTerm)

    if (searchTerm.length > 0) {
      setLoading(true)

      // Check the structure of locations data
      const allLocations = [...locations]

      const filteredLocations = allLocations.filter(
        location =>
          location &&
          location.name &&
          (location.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            (location.postcode && location.postcode.includes(searchTerm))),
      )

      setLocationResults(filteredLocations)
      setShowLocationResults(true)
      setLoading(false)
    } else if (searchTerm.length === 0) {
      const allLocations =
        Array.isArray(locations) && locations.length > 0
          ? locations[0].locations
            ? // If locations is an array of postal code groups
              locations
                .filter(
                  postcodeGroup =>
                    postcodeGroup.locations &&
                    Array.isArray(postcodeGroup.locations),
                )
                .flatMap(postcodeGroup => postcodeGroup.locations)
            : // If locations is already a flat array of locations
              [...locations]
          : []
      setLocationResults(allLocations)
      setShowLocationResults(true)
    } else {
      setShowLocationResults(false)
      setLocationResults([])
    }
  }

  const handleLocationSelect = location => {
    onChange(location)
    setLocationSearch(location.name || '')
    setShowLocationResults(false)
  }

  const handleLocationInputFocus = () => {
    const allLocations = [...locations]

    setLocationResults(allLocations)
    setShowLocationResults(true)
  }

  const handleInputClick = () => {
    setShowLocationResults(true)

    const allLocations = [...locations]

    setLocationResults(allLocations)
  }

  const handleClickOutside = event => {
    if (
      locationSearchRef.current &&
      locationSearchRef.current.contains &&
      typeof locationSearchRef.current.contains === 'function' &&
      !locationSearchRef.current.contains(event.target)
    ) {
      setShowLocationResults(false)
    }
  }

  React.useEffect(() => {
    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  React.useEffect(() => {
    if (value && typeof value === 'object' && value.name) {
      setLocationSearch(value.name)
    } else if (typeof value === 'string') {
      setLocationSearch(value)
    } else {
      setLocationSearch('')
    }
  }, [value])

  const selectStyles = {
    field: {
      width: '364px',
      height: '32px',
      position: 'relative',
    },
    input: {
      padding: '8px 12px 8px 40px',
      fontSize: 14,
      border: '1px solid #E2E2FC',
      borderRadius: 4,
      backgroundColor: '#fff',
      lineHeight: '1.5',
      width: '364px',
      height: '32px',
    },
    icon: {
      position: 'absolute',
      left: 12,
      top: '50%',
      transform: 'translateY(-50%)',
      zIndex: 1,
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: 'max-content',
      // height: '20px',
    },
    iconColor: {
      color: '#707087',
      fontSize: 18,
    },
    zipResults: {
      position: 'absolute',
      top: '100%',
      left: 0,
      right: 0,
      maxHeight: 200,
      overflowY: 'auto',
      zIndex: 99999,
      backgroundColor: '#fff',
      borderRadius: 4,
      marginTop: 2,
      boxShadow: '0 4px 12px rgba(0,0,0,0.3)',
      minHeight: 50,
    },
    placeholder: {
      padding: '12px 16px',
      color: '#707087',
      fontSize: 14,
    },
    locationSelectItem: {
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      width: '100%',
    },
    locationsIcons: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      width: 'max-content',
    },
    locationLabel: {
      display: 'flex',
      alignItems: 'center',
      gap: '6px',
      fontSize: 14,
    },
    listItem: {
      cursor: 'pointer',
      padding: '8px 16px',
      borderBottom: '1px solid #f0f0f0',
    },
  }

  // Get icon only if location is selected
  const selectedLocationIcon = getLocationIcons(value)

  return (
    <div ref={locationSearchRef} style={selectStyles.field}>
      <TextField
        type="text"
        underlineShow={false}
        style={selectStyles.field}
        inputStyle={{
          ...selectStyles.input,
          paddingLeft: selectedLocationIcon.length > 1 ? '64px' : '40px',
        }}
        value={locationSearch}
        onChange={handleLocationSearch}
        onFocus={handleLocationInputFocus}
        onClick={handleInputClick}
        placeholder="Search locations"
      />
      {selectedLocationIcon.length > 0 && (
        <div style={selectStyles.icon}>{selectedLocationIcon}</div>
      )}
      {showLocationResults && (
        <Paper
          style={selectStyles.zipResults}
          elevation={3}
          onClick={e => {
            e.stopPropagation()
          }}
        >
          {loading ? (
            <div style={selectStyles.placeholder}>Loading...</div>
          ) : locationResults.length > 0 ? (
            <List>
              {locationResults.map((location, index) => {
                const icons = getLocationIcons(location)

                const label = (
                  <div style={selectStyles.locationSelectItem}>
                    <div style={selectStyles.locationLabel}>
                      <div style={selectStyles.locationsIcons}>{icons}</div>
                      <div>{location.name}</div>
                    </div>
                    <div>{location.postcode}</div>
                  </div>
                )

                return (
                  <div
                    key={`location-${index}`}
                    onClick={() => {
                      handleLocationSelect(location)
                    }}
                    onMouseEnter={e => {
                      e.currentTarget.style.backgroundColor = '#f5f5f5'
                    }}
                    onMouseLeave={e => {
                      e.currentTarget.style.backgroundColor = '#fff'
                    }}
                    style={{
                      backgroundColor: '#fff',
                      margin: '2px 0',
                      padding: '8px 16px',
                      cursor: 'pointer',
                    }}
                  >
                    {label}
                  </div>
                )
              })}
            </List>
          ) : (
            <div style={selectStyles.placeholder}>No results found</div>
          )}
        </Paper>
      )}
    </div>
  )
}

const MissionDetailsTable = ({
  missions,
  isEdit,
  onClickDeleteLocation = () => {},
  onLocationChange = () => {},
  onMissionBaChange = () => {},
  onMissionTimeChange = () => {},
  onMissionRatingChange = () => {},
  onMissionFeedbackChange = () => {},
  locations,
  dayId,
  cvrPercentiles,
  weatherConditions,
  cityId,
}) => {
  if (!missions || missions.length === 0) {
    return null
  }

  return isEdit ? (
    <div style={rowStyles.detailsTable}>
      <Row style={rowStyles.detailsHeader}>
        <Col xs={5} style={rowStyles.detailsHeaderCell}>
          Location
        </Col>
        <Col xs={2} style={rowStyles.detailsHeaderCell}>
          Time
        </Col>
        <Col
          xs={2}
          style={{ ...rowStyles.detailsHeaderCell, justifyContent: 'center' }}
        >
          BAs per Mission
        </Col>
        <Col
          xs={1}
          style={{ ...rowStyles.detailsHeaderCell, justifyContent: 'center' }}
        />
        <Col
          xs={2}
          style={{ ...rowStyles.detailsHeaderCell, justifyContent: 'center' }}
        />
      </Row>
      {missions.map((mission, index) => (
        <Row
          key={index}
          style={
            index === missions.length - 1
              ? rowStyles.detailsRowLast(isEdit)
              : rowStyles.detailsRow(isEdit)
          }
        >
          <Col xs={5} style={rowStyles.detailsCell}>
            <SelectLocation
              value={{
                name: mission.name,
                isEvent: mission.isEvent,
                isSheltered: mission.isSheltered,
              }}
              onChange={newLocation => {
                onLocationChange(dayId, index, newLocation, cityId)
              }}
              locations={locations}
            />
          </Col>
          <Col xs={2} style={rowStyles.detailsCell}>
            <TimePicker
              value={convertTimeToDisplayFormat(mission.time)}
              onChange={newTime => {
                onMissionTimeChange(dayId, index, newTime)
              }}
            />
          </Col>

          <Col
            xs={2}
            style={{ ...rowStyles.detailsCell, justifyContent: 'center' }}
          >
            <Stepper
              min="1"
              max="99"
              value={mission.basPerMission || 1}
              onAdd={() => {
                const newValue = Math.min((mission.basPerMission || 1) + 1, 99)
                onMissionBaChange(dayId, index, newValue)
              }}
              onSubstract={() => {
                const newValue = Math.max((mission.basPerMission || 1) - 1, 1)
                onMissionBaChange(dayId, index, newValue)
              }}
              onChange={value => {
                const val = Number(value)
                const clamped = Math.min(Math.max(val, 1), 99)
                if (typeof onMissionBaChange === 'function') {
                  onMissionBaChange(dayId, index, clamped)
                } else {
                  console.error(
                    'onMissionBaChange is not a function:',
                    onMissionBaChange,
                  )
                }
              }}
            />
          </Col>
          <Col
            xs={1}
            style={{ ...rowStyles.detailsCell, justifyContent: 'center' }}
          />
          <Col
            xs={2}
            style={{ ...rowStyles.detailsCell, justifyContent: 'center' }}
          >
            <button
              onClick={() => onClickDeleteLocation(mission, dayId)}
              style={rowStyles.deleteButton}
            >
              <TrashCanIcon />
            </button>
          </Col>
        </Row>
      ))}
    </div>
  ) : (
    <div style={rowStyles.detailsTable}>
      <Row style={rowStyles.detailsHeader}>
        <Col xs={4} style={{ ...rowStyles.detailsHeaderCell }}>
          Location
        </Col>
        <Col xs={2} style={rowStyles.detailsHeaderCell}>
          Time
        </Col>
        <Col
          xs={2}
          style={{ ...rowStyles.detailsHeaderCell, justifyContent: 'center' }}
        >
          BAs per Mission
        </Col>
        <Col
          xs={1}
          style={{ ...rowStyles.detailsHeaderCell, justifyContent: 'center' }}
        >
          Weather
        </Col>
        <Col
          xs={2}
          style={{ ...rowStyles.detailsHeaderCell, justifyContent: 'center' }}
        >
          Predicted CVR
        </Col>
        <Col
          xs={1}
          style={{ ...rowStyles.detailsHeaderCell, justifyContent: 'center' }}
        >
          Rating
        </Col>
      </Row>
      {missions.map((mission, index) => {
        const { styles: cvrStyle, cvr } = getCvrGradientStyles(
          mission.predictedCvr,
          cvrPercentiles,
          rowStyles,
        )

        const { weatherCondition, warningWeatherMessage } = weatherConditions[
          mission.locationId
        ]

        const locationNameWrapperStyles = warningWeatherMessage
          ? {
              ...rowStyles.locationNameWrapper,
              ...rowStyles.warningWeatherMessage,
            }
          : rowStyles.locationNameWrapper

        return (
          <Row key={index}>
            <Row
              style={
                index === missions.length - 1
                  ? rowStyles.detailsRowLast(isEdit)
                  : rowStyles.detailsRow(isEdit)
              }
            >
              <Col xs={4} style={locationNameWrapperStyles}>
                <div style={rowStyles.locationIconWrapper}>
                  {getLocationIcons(mission)}
                </div>
                {mission.name}
                <div style={rowStyles.errorWeatherHintWrapper}>
                  {warningWeatherMessage ? (
                    <TooltipModal
                      content={warningWeatherMessage}
                      iconFill="#FFA4B5"
                    />
                  ) : null}
                </div>
              </Col>
              <Col xs={2} style={rowStyles.detailsCell}>
                {getTimeIcon(mission.timeSlot)}
                {formatTimeRange(mission.time)}
              </Col>
              <Col
                xs={2}
                style={{ ...rowStyles.detailsCell, justifyContent: 'center' }}
              >
                {mission.basPerMission}
              </Col>
              <Col
                xs={1}
                style={{ ...rowStyles.detailsCell, justifyContent: 'center' }}
              >
                {getWeatherIcon(weatherCondition)}
              </Col>
              <Col
                xs={2}
                style={{ ...rowStyles.detailsCell, justifyContent: 'center' }}
              >
                <span style={cvrStyle}>{cvr}%</span>
              </Col>
              <Col xs={1} style={rowStyles.ratingCell}>
                <button
                  style={rowStyles.ratingButton(false)}
                  onClick={() => onMissionRatingChange(dayId, index, 'up')}
                >
                  <ThumbUpIcon active={mission.rating === 'up'} />
                </button>
                <button
                  style={rowStyles.ratingButton(false)}
                  onClick={() => onMissionRatingChange(dayId, index, 'down')}
                >
                  <ThumbDownIcon active={mission.rating === 'down'} />
                </button>
              </Col>
            </Row>
            {mission.rating === 'down' ? (
              <Row style={rowStyles.feedbackRow}>
                <Col xs={12}>
                  <div style={rowStyles.feedbackLabel}>Feedback</div>
                  <div
                    style={
                      mission.feedback && mission.feedback.length
                        ? rowStyles.feedbackBox
                        : {
                            ...rowStyles.feedbackBox,
                            border: '1px solid #FFA4B5',
                          }
                    }
                  >
                    <TextField
                      type="text"
                      fullWidth
                      underlineShow={false}
                      inputStyle={{ fontSize: 14 }}
                      hintText="Tell us what should we change to make this mission better."
                      value={mission.feedback || ''}
                      onChange={e =>
                        onMissionFeedbackChange(dayId, index, e.target.value)
                      }
                    />
                  </div>
                  <div style={rowStyles.feedbackLimit}>
                    Character limit: 140
                  </div>
                </Col>
              </Row>
            ) : null}
          </Row>
        )
      })}
    </div>
  )
}

const renderCell = ({
  id,
  fieldName,
  value,
  onClickDeleteDay,
  headers,
  isEdit,
  onRowOpen = () => {},
  isOpened,
  date,
}) => {
  const field = headers.find(h => h.key === fieldName)
  switch (fieldName) {
    case 'date':
      return (
        <Col xs={field.xs} style={rowStyles.dateColumn}>
          {value.replace(/-/g, '.').toUpperCase()}
        </Col>
      )
    case 'morningMissions':
      return (
        <Col xs={field.xs} style={rowStyles.missionColumn}>
          {value}
        </Col>
      )
    case 'lunchMissions':
      return (
        <Col xs={field.xs} style={rowStyles.missionColumn}>
          {value}
        </Col>
      )
    case 'afternoonMissions':
      return (
        <Col xs={field.xs} style={rowStyles.missionColumn}>
          {value}
        </Col>
      )
    case 'details':
      return (
        <Col xs={field.xs} style={rowStyles.buttonColumn}>
          {isEdit ? (
            <button
              onClick={() => onClickDeleteDay({ id, fieldName, value, date })}
              style={rowStyles.deleteButton}
            >
              <TrashCanIcon />
            </button>
          ) : null}
          <button onClick={() => onRowOpen(id)} style={rowStyles.arrowButton}>
            <ArrowIcon position={isOpened ? 'top' : 'bottom'} />
          </button>
        </Col>
      )
    case 'id':
      return null
    default:
      return null
  }
}

const TableRow = ({
  data,
  onClickDeleteDay = () => {},
  onClickDeleteLocation = () => {},
  onLocationChange = () => {},
  onMissionBaChange = () => {},
  onMissionTimeChange = () => {},
  onMissionRatingChange = () => {},
  onMissionFeedbackChange = () => {},
  headers,
  isEdit = false,
  onRowOpen = () => {},
  isOpened,
  locations,
  cvrPercentiles,
  isHaveBadWeather,
  weatherConditions,
  cityId,
}) => {
  const rowWrapperStyles = isHaveBadWeather
    ? {
        ...rowStyles.rowWrapper,
        border: '1px solid #FFA4B5',
      }
    : rowStyles.rowWrapper

  return (
    <Row style={rowWrapperStyles}>
      <Row style={rowStyles.row}>
        {Object.keys(data).map(key => {
          return renderCell({
            id: data.id,
            fieldName: key,
            value: data[key],
            onClickDeleteDay,
            headers,
            isEdit,
            onRowOpen,
            isOpened,
            date: data.date,
          })
        })}
      </Row>
      {isOpened ? (
        <MissionDetailsTable
          onClickDeleteLocation={onClickDeleteLocation}
          onLocationChange={onLocationChange}
          onMissionBaChange={onMissionBaChange}
          onMissionTimeChange={onMissionTimeChange}
          onMissionRatingChange={onMissionRatingChange}
          onMissionFeedbackChange={onMissionFeedbackChange}
          isEdit={isEdit}
          missions={data.missions || []}
          locations={locations}
          dayId={data.id}
          cvrPercentiles={cvrPercentiles}
          weatherConditions={weatherConditions}
          cityId={cityId}
        />
      ) : null}
    </Row>
  )
}

export default TableRow
